import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const links = [
  { label: "Comment ça marche", href: "https://business.ifotolife.shop", external: true },
  { label: "Pourquoi iFotoLife", href: "#pourquoi" },
  { label: "Témoignage", href: "#temoignage" },
  { label: "Contrat", to: "/contract" },
];

export default function Navbar({ onJoin }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-[9999] transition-all duration-300 ${
        scrolled ? "bg-white/80 backdrop-blur-xl border-b border-[#E4E4E7]" : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
          <span className="text-xl md:text-2xl font-black tracking-tighter text-[#0A0A0A]">
            iFoto<span className="text-[#F97316]">Life</span>
          </span>
          <span className="hidden sm:inline text-[10px] font-black uppercase tracking-label text-[#A1A1AA] mt-1">
            Partenaires
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) =>
            l.to ? (
              <Link
                key={l.label}
                to={l.to}
                className="text-sm font-medium text-[#52525B] hover:text-[#0A0A0A] transition-colors"
                data-testid={`nav-link-${l.label}`}
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.label}
                href={l.href}
                className="text-sm font-medium text-[#52525B] hover:text-[#0A0A0A] transition-colors"
                data-testid={`nav-link-${l.label}`}
              >
                {l.label}
              </a>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={onJoin}
            className="hidden md:inline-flex items-center gap-1.5 bg-[#F97316] hover:bg-[#EA580C] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-all duration-300 hover:gap-2.5"
            data-testid="nav-cta-button"
          >
            Devenir partenaire <ArrowUpRight className="w-4 h-4" />
          </button>
          <button
            className="md:hidden p-2 text-[#0A0A0A]"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-white border-b border-[#E4E4E7]"
          >
            <div className="px-5 py-4 flex flex-col gap-4">
              {links.map((l) =>
                l.to ? (
                  <Link key={l.label} to={l.to} onClick={() => setOpen(false)} className="text-base font-medium text-[#27272A]">
                    {l.label}
                  </Link>
                ) : (
                  <a key={l.label} href={l.href} onClick={() => setOpen(false)} className="text-base font-medium text-[#27272A]">
                    {l.label}
                  </a>
                )
              )}
              <button
                onClick={() => { setOpen(false); onJoin?.(); }}
                className="mt-2 bg-[#F97316] text-white text-sm font-semibold px-5 py-3 rounded-full"
                data-testid="nav-mobile-cta"
              >
                Devenir partenaire
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
